import {
  CalendarIcon,
  MapPinIcon,
  MessageCircle,
  PencilIcon,
  PhoneIcon,
  UserIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"

import { WalletTab } from "@/components/customers/wallet-tab"
import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import {
  Button,
  buttonVariants,
} from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { FRONTEND_ROUTES } from "@/constants"
import { usePermission } from "@/contexts/permission"
import { PermissionsRolesEnum } from "@/enums"
import { formatDocument } from "@/helpers/format"
import { useCustomerById } from "@/hooks/use-customer-by-id"
import { cn } from "@/lib/utils"
import { DateHelper } from "@/utils"

import { CustomerDetailsSkeleton } from "./customer-details-skeleton"

export const CustomerDetails = () => {
  const { t } = useTranslation()
  const { customerId } = useParams<{ customerId: string }>()
  const { data, isFetching, error } = useCustomerById(
    customerId!
  )
  const { hasPermission, isSuperAdmin } = usePermission()

  const canWrite = hasPermission([
    PermissionsRolesEnum.CUSTOMERS_WRITE,
  ])

  // A carteira traz portador, documento e bandeira do cartão: aba de suporte
  // INTERNO, restrita ao staff Pay2B. `hasPermission` não serve aqui — devolve
  // true para o admin do MERCHANT, que não pode ver isto. Esconder é
  // conveniência; quem barra de verdade é o scope ["superAdmin"] no gateway.
  const canSeeWallet = isSuperAdmin

  const [activeTab, setActiveTab] = useState("details")

  const hasData = Boolean(data)
  const createdAt = data?.createdAt
    ? DateHelper.format(
        data.createdAt,
        "dd/MM/yyyy 'às' HH:mm"
      )
    : undefined
  const updatedAt = data?.updatedAt
    ? DateHelper.format(
        data.updatedAt,
        "dd/MM/yyyy 'às' HH:mm"
      )
    : undefined
  const phoneNumber =
    data?.phone?.countryCode &&
    data?.phone?.areaCode &&
    data?.phone?.number
      ? `+${data.phone.countryCode} (${data.phone.areaCode}) ${data.phone.number}`
      : undefined

  const whatsappNumber =
    data?.phone?.countryCode &&
    data?.phone?.areaCode &&
    data?.phone?.number
      ? `${data.phone.countryCode}${data.phone.areaCode}${data.phone.number}`
      : undefined
  const whatsappLink = whatsappNumber
    ? `https://wa.me/${whatsappNumber}`
    : undefined

  const title =
    data?.firstName && data?.lastName
      ? `${data.firstName} ${data.lastName}`
      : (error?.message ??
        t("pages.customers.customerDetails"))

  if (isFetching) {
    return <CustomerDetailsSkeleton />
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-sm font-semibold text-muted-foreground">
              {t("pages.customers.customerDetails")}
            </h2>
            {hasData && (
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="page-title">{title}</h1>
              </div>
            )}
          </div>
          {hasData && canWrite && (
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`${FRONTEND_ROUTES.CUSTOMERS.href}/edit/${customerId}`}
                className={cn(
                  buttonVariants({
                    variant: "outline",
                    size: "sm",
                  }),
                  "gap-1.5"
                )}
              >
                <PencilIcon size={14} />
                {t("pages.customers.actions.edit")}
              </Link>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasData && (
          <section className="rounded-md border border-destructive/40 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              {error?.message ??
                t("pages.customers.details.loadError")}
            </p>
          </section>
        )}
        {hasData && (
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="mb-2">
              <TabsTrigger value="details">
                {t("pages.customers.details.tabs.details")}
              </TabsTrigger>
              {canSeeWallet && (
                <TabsTrigger value="wallet">
                  {t("pages.customers.details.tabs.wallet")}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="details">
              <section className="grid gap-4 lg:grid-cols-2">
                <SectionCard
                  title={t(
                    "pages.customers.details.personalInfo"
                  )}
                  icon={<UserIcon size={16} />}
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <InfoItem
                      label="ID"
                      value={data?.id}
                      copyable
                    />
                    <InfoItem
                      label={t(
                        "pages.customers.table.name"
                      )}
                      value={
                        data
                          ? `${data.firstName} ${data.lastName}`
                          : undefined
                      }
                    />
                    <InfoItem
                      label={t(
                        "pages.customers.table.document"
                      )}
                      value={
                        data?.document
                          ? formatDocument(data.document)
                          : undefined
                      }
                      copyable
                    />
                    <InfoItem
                      label={t("common.documentType")}
                      value={data?.documentType}
                    />
                    <InfoItem
                      label={t(
                        "pages.customers.table.email"
                      )}
                      value={data?.email}
                      copyable
                      truncate
                    />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-xs font-medium text-muted-foreground">
                        {t("pages.customers.table.phone")}
                      </span>
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm">
                          {phoneNumber ||
                            t("common.notAvailable")}
                        </span>
                        {data?.phone &&
                          (data.phone.whatsApp &&
                          whatsappLink ? (
                            <Button
                              variant="outline"
                              size="icon"
                              asChild
                              className="h-7 w-7 shrink-0"
                            >
                              <a
                                href={whatsappLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <MessageCircle
                                  size={14}
                                  className="text-green-600"
                                />
                              </a>
                            </Button>
                          ) : (
                            <PhoneIcon
                              size={14}
                              className="text-muted-foreground"
                            />
                          ))}
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  title={t(
                    "pages.customers.details.addressInfo"
                  )}
                  icon={<MapPinIcon size={16} />}
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <InfoItem
                      label={t(
                        "pages.customers.details.street"
                      )}
                      value={
                        data?.address
                          ? `${data.address.street}, ${data.address.streetNumber}`
                          : undefined
                      }
                    />
                    <InfoItem
                      label={t("common.complement")}
                      value={data?.address?.streetNumber}
                    />
                    <InfoItem
                      label={t(
                        "pages.customers.details.neighborhood"
                      )}
                      value={data?.address?.neighborhood}
                    />
                    <InfoItem
                      label={t(
                        "pages.customers.details.city"
                      )}
                      value={
                        data?.address
                          ? `${data.address.city} - ${data.address.state}`
                          : undefined
                      }
                    />
                    <InfoItem
                      label={t("common.zipCode")}
                      value={data?.address?.zipCode}
                    />
                    <InfoItem
                      label={t(
                        "pages.customers.details.country"
                      )}
                      value={data?.address?.country}
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  title={t(
                    "pages.customers.details.systemInfo"
                  )}
                  icon={<CalendarIcon size={16} />}
                  className="lg:col-span-2"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <InfoItem
                      label={t(
                        "pages.customers.details.createdAt"
                      )}
                      value={createdAt}
                    />
                    <InfoItem
                      label={t(
                        "pages.customers.details.updatedAt"
                      )}
                      value={updatedAt}
                    />
                  </div>
                </SectionCard>
              </section>
            </TabsContent>

            {canSeeWallet && (
              <TabsContent value="wallet">
                {/* Só busca ao abrir a aba: dado sensível não é carregado sem
                    o operador pedir. */}
                {activeTab === "wallet" && customerId && (
                  <WalletTab customerId={customerId} />
                )}
              </TabsContent>
            )}
          </Tabs>
        )}
      </CardContent>
    </Card>
  )
}
